



































































































































import { isViewAsSelected } from "@/store/modules/TargetUserModule";
import {
  Ref,
  Component,
  Prop,
  Vue as VueDecorator,
  Watch,
} from "vue-property-decorator";
import { isDryingProject } from "@/data/repository/ProjectRepository";
import { UserModule } from "@/store/modules/UserModule";
import {
  ProjectDetails,
  ProjectNameSource,
  ExtendedProjectNameSource,
  RegulatorType,
  ContactPersonInfo,
  ApprovalResult,
  getProjectOwnerId,
  isPortalOnlyProject,
  formatProjectTypeName,
  getProjectTypeIdOfProject,
  ProjectTypeConfig,
  getDefaultProjectTypes,
} from "@/data/models/ProjectDetails";

import { ProjectManagement } from "@/data/models/Client";

import { generateRandomUuid } from "@/utils/Utils";
import { buildIresProjectNumber } from "@/utils/ProjectOverviewUtils";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";

import StateButtonComponent, {
  ButtonState,
} from "@/components/common/StateButton.vue";

import { formatDateLong, formatDateAndTime } from "@/utils/DateFormatter";
import { ProjectStatusData } from "@/data/models/ProjectStatusData";

import clone from "clone";

import ProjectStatusComponent from "@/components/project/status/ProjectStatus.vue";
import ClientDataSectionComponent from "./ClientDataSection.vue";
import InsuranceCaseSectionComponent from "./InsuranceCaseSection.vue";
import RegulationSectionComponent from "./RegulationSection.vue";
import PolicyHolderSectionComponent from "./PolicyHolderSection.vue";
import DamageLocationSectionComponent from "./DamageLocationSection.vue";
import ContactPersonSectionComponent from "./ContactPersonSection.vue";
import ContactSectionComponent from "./ContactSection.vue";
import ProjectSectionComponent from "./ProjectSection.vue";
import SetProjectOwnershipComponent from "@/components/project/detail/SetProjectOwnership.vue";

import { firestore } from "firebase/app";

import {
  localiseRegulatorType,
  formatNameInfo,
  formatLocationInfo,
  formatCompleteContactPersonInfo,
  formatContactInfo,
  formatCompleteContactInfo,
} from "@/utils/LocalisationHelper";
import router from "@/router/router";
import {
  doesClientHaveApp,
  findBranch,
  getCurrentIresClientType,
  getProjectManagement,
  getTargetAppVersion,
  isBelforClient,
  isSimulatingErp,
  getClientId
} from "@/data/repository/ClientRepository";
import { getIresClientType, IresClientType } from "@/data/models/IresClient";
import { getActiveUser, getRecordInfo } from "@/data/repository/UserRepository";
import { alwaysAllowedToEditProjectAccess } from "@/permissions/UserPermissions";
import { Url } from "url";
import { getPdfDocument } from "@/utils/pdf/PdfDocumentRepository";
import { getProjectSettingsV2, ProjectSettingsOptions } from "@/data/models/Settings";
import { ProjectManagementSettingsModule } from "@/store/modules/ProjectManagementSettingsModule";
import { COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { themed } from "@/utils/Theme";
import { calculateProjectName, ProjectNameInput } from "@/utils/ProjectNameCalculation";
import { createProjectEventLog, ProjectEventType, ContactPersonEventInfo } from "@/data/models/ProjectLog";
import { addProjectEventLog } from "@/data/repository/ProjectLogRepository";

@Component({
  components: {
    "set-project-owner": SetProjectOwnershipComponent,
    "insurance-case": InsuranceCaseSectionComponent,
    regulation: RegulationSectionComponent,
    "policy-holder": PolicyHolderSectionComponent,
    "contact-section": ContactSectionComponent,
    "damage-location": DamageLocationSectionComponent,
    "contact-person": ContactPersonSectionComponent,
    "project-section": ProjectSectionComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
    "client-data-section": ClientDataSectionComponent,
    "state-button": StateButtonComponent,
    "project-status": ProjectStatusComponent,
  },
})
export default class ClientDataDetailsComponent extends VueDecorator {
  @Ref("setProjectOwner")
  setProjectOwnershipComponent!: SetProjectOwnershipComponent;

  @Ref("insuranceCase")
  insuranceCaseSectionComponent!: InsuranceCaseSectionComponent;

  @Ref("regulation")
  regulationSectionComponent!: RegulationSectionComponent;

  @Ref("policyHolder")
  policyHolderSectionComponent!: PolicyHolderSectionComponent;

  @Ref("damageLocation")
  damageLocationSectionComponent!: DamageLocationSectionComponent;

  @Ref("contact")
  contactSectionComponent!: ContactSectionComponent;

  @Ref("confirm")
  confirmationDialogComponent!: ConfirmationDialogComponent;

  @Ref("projectSection")
  projectSectionComponent!: ProjectSectionComponent;

  @Ref("contactPerson")
  contactPersonSectionComponent!: ContactPersonSectionComponent;

  @Prop({ default: null })
  projectStatus!: ProjectStatusData | null;

  @Prop()
  projectDetails!: ProjectDetails;

  @Prop()
  isNewProject!: boolean;

  @Prop({ default: true })
  canEditProject!: boolean;

  @Prop({ default: false })
  isReadOnlyMode!: boolean;

  @Prop({ default: null })
  earliestAssignmentDate!: firestore.Timestamp | null;

  additionalPdfDocumentLink: Url | null = null;
  additionalPdfDocumentLink2: Url | null = null;

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  get activeUser() {
    return getActiveUser();
  }

  get isAlwaysAllowedToEditProjectAccess() {
    return alwaysAllowedToEditProjectAccess(this.activeUser);
  }

  // BELFOR edits contacts without the edit right, but never while "Sicht auf" is active (ticket 2610051).
  get canEditContact() {
    return (this.canEditProject || isBelforClient()) && !isViewAsSelected();
  }

  get canEditContactPersons() {
    return (this.canEditProject || isBelforClient()) && !isViewAsSelected();
  }

  projectManagement = getProjectManagement();

  get isProjectRejected() {
    return this.clientData.projectApproval?.approvalResult === ApprovalResult.REJECTED;
  }

  get isProjectWebsiteOnly() {
    return (
      this.projectManagement === ProjectManagement.WEBSITE ||
      (this.projectDetails ? isPortalOnlyProject(this.projectDetails) : false)
    );
  }

  get showProjectStatus() {
    return doesClientHaveApp();
  }

  get additionalPdfDocumentIntroText() {
    if (!this.hasBothAdditionalPdfDocuments) {
      return "";
    }

    return `${this.$t("clientData.pdfDocumentInfo").toString()}:`;
  }

  get additionalPdfDocumentText() {
    if (!this.projectDetails.pdfDocument && !this.projectDetails.pdfDocument2) {
      return "";
    }

    if (this.hasBothAdditionalPdfDocuments) {
      return this.projectDetails.pdfDocument ?? "";
    }

    return this.$t("clientData.pdfDocumentInfo").toString();
  }

  get additionalPdfDocumentText2() {
    if (!this.hasBothAdditionalPdfDocuments) {
      return "";
    }

    return this.projectDetails.pdfDocument2 ?? "";
  }

  get additionalPdfDocumentPrimaryLink() {
    if (this.hasBothAdditionalPdfDocuments || this.projectDetails.pdfDocument) {
      return this.additionalPdfDocumentLink;
    }

    return this.additionalPdfDocumentLink2;
  }

  get additionalPdfDocumentSecondaryLink() {
    if (!this.hasBothAdditionalPdfDocuments) {
      return null;
    }

    return this.additionalPdfDocumentLink2;
  }

  get hasBothAdditionalPdfDocuments() {
    return !!this.projectDetails.pdfDocument && !!this.projectDetails.pdfDocument2;
  }

  get showInsuranceCaseInfo() {
    return !this.projectDetails.clientData.directOrder;
  }

  async onOpenContactPersonSection(contactPersonInfo: ContactPersonInfo) {
    const index = this.clientData.contactPersons.indexOf(contactPersonInfo);

    const contactPersonInfoClone = clone(contactPersonInfo);

    const result = await this.contactPersonSectionComponent.show(
      contactPersonInfoClone,
      this.projectDetails
    );

    if (result) {
      result.lastEditedBy = getRecordInfo();
      const projectDetailsClone = clone(this.projectDetails);
      projectDetailsClone.clientData.contactPersons[index] = result;
      this.onProjectChanged(projectDetailsClone);
    }
  }

  async openSectionDialog(
    promise: (
      projectDetails: ProjectDetails
    ) => Promise<ProjectDetails | undefined>,
    closeIfNoResult?: boolean
  ) {
    const result = await promise(clone(this.projectDetails));

    if (result) {
      this.onProjectChanged(result);
    } else if (closeIfNoResult) {
      router.back();
    }
  }

  onProjectChanged(change: ProjectDetails) {
    this.$emit("projectDetailsChange", this.setProjectName(change));
  }

  setProjectName(projectDetails: ProjectDetails): ProjectDetails {
    const extendedSource = projectDetails.clientData.extendedProjectNameSource;
    const source = extendedSource ?? projectDetails.clientData.projectNameSource;

    if (source === ProjectNameSource.USER_INPUT || source === ExtendedProjectNameSource.USER_INPUT) {
      if (
        !projectDetails.clientData.projectName ||
        projectDetails.clientData.projectName === ""
      ) {
        return this.setProjectNameFromCalculatedParts(projectDetails);
      } else {
        return projectDetails;
      }
    }

    return this.setProjectNameFromCalculatedParts(projectDetails);
  }

  setProjectNameFromCalculatedParts(projectDetails: ProjectDetails) {
    const result = this.calculateProjectNameFromParts(projectDetails);

    if (result) {
      const { name, source, extendedSource } = result;

      projectDetails.clientData.projectName = name;
      projectDetails.clientData.projectNameSource = source;
      projectDetails.clientData.extendedProjectNameSource = extendedSource;
    } else {
      projectDetails.clientData.projectName = "";
      projectDetails.clientData.projectNameSource =
        ProjectNameSource.PROJECT_NAME_OFFICE;
      projectDetails.clientData.extendedProjectNameSource =
        ExtendedProjectNameSource.PROJECT_NAME_OFFICE;
    }

    return projectDetails;
  }

  calculateProjectNameFromParts(projectDetails: ProjectDetails): { name: string; source: ProjectNameSource; extendedSource: ExtendedProjectNameSource } | null {
    const settings = ProjectManagementSettingsModule.projectManagementSettings;
    const template = settings?.projectNameTemplate;

    const input: ProjectNameInput = {
      projectNumber: projectDetails.clientData.projectNumber,
      projectNumberIres: projectDetails.clientData.projectNumberIres,
      damageLocationInfo: projectDetails.clientData.damageLocationInfo,
      contactPersons: projectDetails.clientData.contactPersons,
      contact: projectDetails.clientData.contact,
    };

    return calculateProjectName(input, template);
  }

  calculateProjectNameFromContactPerson(projectDetails: ProjectDetails) {
    const contactPersonInfo = projectDetails.clientData.contactPersons.filter(
      (contactPerson) => contactPerson.contactPersonNumber === 1
    )[0];

    if (!contactPersonInfo) {
      return null;
    }

    const name = contactPersonInfo.lastName;
    const location = projectDetails.clientData.damageLocationInfo.city;
    const street = projectDetails.clientData.damageLocationInfo.street;

    const parts = this.filterStrings([name, location, street]);

    if (parts.length < 2) {
      if (contactPersonInfo.lastName && contactPersonInfo.lastName !== "") {
        return contactPersonInfo.lastName;
      } else {
        return null;
      }
    } else {
      return this.joinToString(parts, "/");
    }
  }

  @Watch("projectDetails.pdfDocument")
  async onProjectPdfDocumentChanged() {
    if (!this.projectDetails.pdfDocument) {
      this.additionalPdfDocumentLink = null;
    }
    else {
      this.additionalPdfDocumentLink = null;
      const pdfDocumentName = this.projectDetails.pdfDocument;

      setTimeout(() => {
        this.getPdfDocument(pdfDocumentName);
      }, 800);
    } 
  }

  @Watch("projectDetails.pdfDocument2")
  async onProjectPdfDocument2Changed() {
    if (!this.projectDetails.pdfDocument2) {
      this.additionalPdfDocumentLink2 = null;
    }
    else {
      this.additionalPdfDocumentLink2 = null;
      const pdfDocumentName = this.projectDetails.pdfDocument2;

      setTimeout(() => {
        this.getPdfDocument2(pdfDocumentName);
      }, 800);
    }
  }

  async getPdfDocument(fileName: string) {
    this.additionalPdfDocumentLink = await getPdfDocument(fileName); 
  }

  async getPdfDocument2(fileName: string) {
    this.additionalPdfDocumentLink2 = await getPdfDocument(fileName);
  }

  async mounted() {
    if (this.isNewProject) {
      await this.openProjectSection();
    }

    if (this.projectDetails.pdfDocument && !this.additionalPdfDocumentLink) {
      await this.getPdfDocument(this.projectDetails.pdfDocument);
    }

    if (this.projectDetails.pdfDocument2 && !this.additionalPdfDocumentLink2) {
      await this.getPdfDocument2(this.projectDetails.pdfDocument2);
    }
  }

  async openRegulationSection() {
    await this.openSectionDialog(this.regulationSectionComponent.show);
  }

  async openInsucranceCaseSection() {
    await this.openSectionDialog(this.insuranceCaseSectionComponent.show);
  }

  async openPolicyHolderSection() {
    await this.openSectionDialog(this.policyHolderSectionComponent.show);
  }

  async openDamageLocationSection() {
    await this.openSectionDialog(this.damageLocationSectionComponent.show);
  }

  async openContactSection() {
    await this.openSectionDialog(this.contactSectionComponent.show);
  }

  async openProjectSection() {
    await this.openSectionDialog(
      this.projectSectionComponent.show,
      this.isNewProject
    );
  }

  async onSetProjectOwnership() {
    const result = await this.setProjectOwnershipComponent.show(
      this.projectDetails
    );

    if (result) {
      this.$emit("projectOwnershipChanged", result);
    }
  }

  get clientData() {
    return this.projectDetails.clientData;
  }

  get unfilteredProjectTypes() {
    const fromStore = this.projectManagementSettings;
    const loaded: ProjectTypeConfig[] | undefined = fromStore && fromStore.projectTypes;
    const base = JSON.parse(JSON.stringify(getDefaultProjectTypes())) as ProjectTypeConfig[];

    if (loaded && Array.isArray(loaded)) {
      const map = new Map<number, ProjectTypeConfig>(loaded.map(p => [p.projectTypeId, p]));
      return base.map(p => {
        const saved = map.get(p.projectTypeId);
        return saved ? { ...p, ...saved } : p;
      });
    } else {
      return base;
    }
  }

  getProjectTypesText() {
    const projectTypeId = getProjectTypeIdOfProject(this.projectDetails);
    const formattedProjectType = formatProjectTypeName(this, projectTypeId, this.unfilteredProjectTypes);

    return this.$t("selectProjectType.formatProjectType", [formattedProjectType]).toString();
  }

  get projectOwnerSectionText() {
    const projectOwnerId = getProjectOwnerId(this.projectDetails);
    const projectSupervisorId = this.projectDetails.projectSupervisorId;
    
    const projectOwnerStaffNumber = this.projectDetails.projectOwnerStaffNumber;
    const projectSupervisorStaffNumber = this.projectDetails.projectSupervisorStaffNumber;

    const result: string[] = [];

    if (projectOwnerId) {
      const projectOwnerName = UserModule.user(projectOwnerId)?.nickname;

      if (projectOwnerName) {
        result.push(
          this.$t("clientData.formatProjectOwner", [
            projectOwnerName,
          ]).toString()
        );
      }
      else if (projectOwnerStaffNumber) {
        result.push(
          this.$t("clientData.formatProjectOwner", [
            UserModule.userNicknameByStaffNumber(projectOwnerStaffNumber),
          ]).toString()
        );
      }
      else {
        result.push(
          this.$t("clientData.formatProjectOwner", [
            "????",
          ]).toString()
        );
      }
    }

    if (projectSupervisorId) {
      const projectSupervisorName = UserModule.user(projectSupervisorId)
        ?.nickname;

      if (projectSupervisorName) {
        result.push(
          this.$t("clientData.formatProjectSupervisor", [
            projectSupervisorName,
          ]).toString()
        );
      }
      else if (projectSupervisorStaffNumber) {
        result.push(
          this.$t("clientData.formatProjectSupervisor", [
            UserModule.userNicknameByStaffNumber(projectSupervisorStaffNumber),
          ]).toString()
        );
      }
      else {
        result.push(
          this.$t("clientData.formatProjectSupervisor", [
            "????",
          ]).toString()
        );
      }
    }

    return result.join("\n");
  }

  get canCreateAppointmentsInPortal() {
    return this.projectSettings.newAppointmentsIn.includes(ProjectSettingsOptions.PORTAL);
  }

  get projectOwnerSectionState() {
    if (this.isReadOnlyMode || !this.canCreateAppointmentsInPortal) {
      return ButtonState.DISABLED;
    }

    if (!this.canEditProject && !this.isAlwaysAllowedToEditProjectAccess) {
      return ButtonState.DISABLED;
    }

    if (!getProjectOwnerId(this.projectDetails) && !this.projectDetails.projectSupervisorId) {
      return ButtonState.STEP_BUTTON_OVERDUE;
    }
    else if (!getProjectOwnerId(this.projectDetails) || !this.projectDetails.projectSupervisorId) {
      return ButtonState.STEP_BUTTON_INCOMPLETE;
    }
    else {
      return ButtonState.STEP_BUTTON_COMPLETED;
    }
  }

  get isSanTecClient() {
    return getIresClientType(getClientId()) === IresClientType.SanTec;
  }

  get projectSection() {
    const clientData = this.clientData;

    const projectTyes = this.getProjectTypesText();
    let branchDetails: string | undefined = undefined;

    if (clientData.branch) {
      const branch = findBranch(clientData.branch);

      if (branch) {
        branchDetails = this.$t("clientData.formatBranch", [
          branch.name,
        ]).toString();
      }
    }

    const isSanTec = this.isSanTecClient;
    const projectStartDateForSanTec = clientData.projectApproval?.recordInfo.recordedAt;
    const isProjectStartDateForSanTecValid = 
      projectStartDateForSanTec && 
      projectStartDateForSanTec.toMillis() < clientData.projectStartDate?.toMillis();

    const projectStartDate = isSanTec && isProjectStartDateForSanTecValid
      ? projectStartDateForSanTec
      : clientData.projectStartDate;

    const projectDate = this.$t("clientData.formatProjectStart", [
      formatDateAndTime(projectStartDate),
    ]).toString();

    const iresClientType = getCurrentIresClientType();

    const projectNumber = clientData.projectNumber || (isSimulatingErp() && clientData.projectNumberIres)
      ? this.$t(
          iresClientType === IresClientType.Other
            ? "clientData.formatProjectNumberOffice"
            : "clientData.mainContractNumber",
          [isSimulatingErp() ? clientData.projectNumberIres : clientData.projectNumber]
        ).toString()
      : "";

    const projectNameErp =
      clientData.projectNameErp
        ? clientData.projectNumber 
          ? this.$t("clientData.formatProjectNumberOfficeForSantec", [
              clientData.projectNumber,
              clientData.projectNameErp
            ]).toString()
          : this.$t("clientData.formatProjectNumberOffice", [
              isSimulatingErp() ? clientData.projectNumberIres : clientData.projectNameErp
            ]).toString()
        : undefined;

    const projectNumberIres = !clientData.projectNumberIres && (isSimulatingErp() && !clientData.projectName)
      ? undefined
      : this.$t("clientData.formatProjectNumberIres", [
          isSimulatingErp() ? clientData.projectNumber : clientData.projectNumberIres,
        ]).toString();

    const lines = 
      projectNameErp 
      ? [
        projectDate,
        branchDetails,
        projectTyes,
        projectNameErp,
        projectNumberIres,
      ]
      : [
        projectDate,
        branchDetails,
        projectTyes,
        projectNumber,
        projectNumberIres,
      ];

    if (clientData.startedBy) {
      const startedByDate = formatDateAndTime(
        clientData.startedBy.recordedAt,
        clientData.startedBy.recordedAt
      );

      const startedByUser = UserModule.userNicknameFromRecordInfo(
        clientData.startedBy
      );

      const startedByLine = this.$t("clientData.createdAt", [
        startedByDate,
        startedByUser,
      ]).toString();

      const projectApproval = clientData.projectApproval;

      if (
        projectApproval &&
        (
          projectApproval.approvalResult === ApprovalResult.APPROVED ||
          projectApproval.approvalResult === ApprovalResult.REJECTED
        )
      ) {
        const approvedByDate = formatDateAndTime(
          projectApproval.recordInfo.recordedAt,
          projectApproval.recordInfo.recordedAt
        );

        const approvedBySeconds = projectApproval.recordInfo?.recordedAt?.seconds;
        const startedBySeconds = clientData.startedBy?.recordedAt?.seconds;

        const approvedByUser = UserModule.userNicknameFromRecordInfo(
          projectApproval.recordInfo
        );

        if (
          approvedByDate !== startedByDate ||
          approvedByUser !== startedByUser ||
          approvedBySeconds !== startedBySeconds
        ) {
          lines.push(
            this.$t(
              projectApproval.approvalResult ===
                ApprovalResult.APPROVED
                ? "clientData.approvedBy"
                : "clientData.rejectedBy",
              [approvedByDate, approvedByUser]
            ).toString()
          );
        }
      } else {
        lines.push(startedByLine);
      }
    }

    return this.joinWithNewline(lines);
  }

  get projectManagementSettings() {
    return ProjectManagementSettingsModule.projectManagementSettings;
  }

  get projectSettings() {
    return getProjectSettingsV2(this.projectManagementSettings);
  }

  get canCreateProjectInPortal() {
    return this.projectSettings.newProjectsIn.includes(ProjectSettingsOptions.PORTAL); 
  }

  get canEditProjectDataInPortal() {
    return this.projectSettings.editProjectsIn.includes(ProjectSettingsOptions.PORTAL); 
  }

  get projectSectionState() {
    if (this.isReadOnlyMode || !this.canCreateProjectInPortal) {
      return ButtonState.DISABLED;
    }

    if (!this.canEditProject && !this.isAlwaysAllowedToEditProjectAccess) {
      return ButtonState.DISABLED;
    }

    const approvalResult = this.clientData.projectApproval?.approvalResult;
    if (
      approvalResult !== ApprovalResult.APPROVED &&
      approvalResult !== ApprovalResult.REJECTED
    ) {
      return ButtonState.STEP_BUTTON_INCOMPLETE;
    }

    return this.calculateSectionState(this.projectSection, {
      strict: true,
      enableWhenCreatingNewProject: true,
    });
  }

  get damageLocationSection() {
    const clientData = this.clientData;

    return formatLocationInfo(clientData.damageLocationInfo);
  }

  get damageLocationSectionState() {
    if (!this.canEditProjectDataInPortal) {
      return ButtonState.DISABLED;
    }

    return this.calculateSectionState(this.damageLocationSection);
  }

  get contactSection() {
    const contact = this.clientData.contact;

    return contact ? formatCompleteContactInfo(this, contact) : undefined;
  }

  get contactSectionState() {
    if (!this.canEditProjectDataInPortal) {
      return ButtonState.DISABLED;
    }

    return this.calculateSectionState(this.contactSection, {
      strict: false,
      canEditSection: this.canEditContact,
    });
  }

  get policyHolderSection() {
    const clientData = this.clientData;

    return this.joinWithNewline([
      formatNameInfo(clientData.policyHolderInfo),
      formatLocationInfo(clientData.policyHolderInfo),
      formatContactInfo(this, clientData.policyHolderInfo),
    ]);
  }

  get policyHolderSectionState() {
    if (!this.canEditProjectDataInPortal) {
      return ButtonState.DISABLED;
    }

    return this.calculateSectionState(this.policyHolderSection);
  }

  get insuranceCaseSection() {
    const clientData = this.clientData;

    return this.joinToString(
      [
        this.joinToString(
          [
            clientData.insuranceName,
            clientData.additionalInsuranceInfo,
          ],
          " / "
        ),
        this.joinToString(
          [
            this.formatString(
              clientData.policyNumber,
              "clientData.policy"
            ),
            this.formatString(
              clientData.damageId,
              "clientData.damageId"
            ),
          ],
          ", "
        ),
        this.joinToString(
          [
            this.formatString(
              clientData.damageNumber,
              "clientData.damage"
            ),
            this.formatString(clientData.eventNumber, "clientData.process"),
          ],
          ", "
        ),
        this.joinToString(
          [
            this.formatString(
              clientData.damageType,
              "clientData.damageType",
              true
            ),
            this.dateWithFormat(clientData.damageDay, "clientData.damageDay"),
          ],
          ", "
        ),
      ],
      "\n"
    );
  }

  get insuranceCaseSectionState() {
    if (!this.canEditProjectDataInPortal) {
      return ButtonState.DISABLED;
    }

    return this.calculateSectionState(this.insuranceCaseSection);
  }

  get regulationSection() {
    const clientData = this.clientData;

    if (!clientData.regulatorInfo?.regulatorType) {
      return "";
    }

    return this.joinWithNewline([
      this.formatString(
        localiseRegulatorType(this, clientData.regulatorInfo.regulatorType),
        "clientData.regulationBy"
      ),
      formatNameInfo(clientData.regulatorInfo),
      formatContactInfo(this, clientData.regulatorInfo),
    ]);
  }

  get regulationSectionState() {
    if (!this.canEditProjectDataInPortal) {
      return ButtonState.DISABLED;
    }

    return this.calculateSectionState(this.regulationSection, {
      strict: false,
    });
  }

  joinWithNewline(toJoin: Array<string | undefined | null>) {
    return this.joinToString(toJoin, "\n");
  }

  joinToString(toJoin: Array<string | undefined | null>, separator: string) {
    return this.filterStrings(toJoin).join(separator);
  }

  filterStrings(toJoin: Array<string | undefined | null>) {
    return toJoin.filter((value) => value && value !== "");
  }

  formatString(
    value: string | undefined | null,
    format: string,
    addColon?: boolean
  ) {
    if (!value || value === "") {
      return undefined;
    }

    const s = this.$t(format).toString();

    if (addColon) {
      return `${s}: ${value}`;
    } else {
      return `${s} ${value}`;
    }
  }

  dateWithFormat(
    timestamp: firestore.Timestamp | undefined | null,
    format: string
  ) {
    if (!timestamp) {
      return undefined;
    }

    const s = this.$t(format);

    return `${s}: ${formatDateLong(timestamp)}`;
  }

  calculateSectionState(
    value?: string,
    options?: {
      strict?: boolean;
      enableWhenCreatingNewProject?: boolean;
      canEditSection?: boolean;
    }
  ) {
    if (this.isReadOnlyMode) {
      return ButtonState.DISABLED;
    }

    const { canEditSection, strict, enableWhenCreatingNewProject } =
      options ?? {};

    if (canEditSection !== undefined) {
      if (!canEditSection && !this.isAlwaysAllowedToEditProjectAccess) {
        return ButtonState.DISABLED;
      }
    } else if (!this.canEditProject && !this.isAlwaysAllowedToEditProjectAccess) {
      return ButtonState.DISABLED;
    }

    if (!enableWhenCreatingNewProject && this.isNewProject) {
      return ButtonState.DISABLED;
    }

    const isStrict = strict || strict === undefined;

    if (!value || value === "") {
      if (isStrict) {
        return ButtonState.STEP_BUTTON_OVERDUE;
      } else {
        return ButtonState.STEP_BUTTON_INCOMPLETE;
      }
    }

    return ButtonState.STEP_BUTTON_COMPLETED;
  }

  contactPersonTitle(contactPerson: ContactPersonInfo) {
    const contactPersonString = this.$t("clientData.contactPerson");
    if (this.clientData.contactPersons.length === 1) {
      return contactPersonString;
    } else {
      return contactPersonString + " " + contactPerson.contactPersonNumber;
    }
  }

  contactPersonSection(contactPerson: ContactPersonInfo) {
    return formatCompleteContactPersonInfo(this, contactPerson);
  }

  contactPersonSectionState(contactPerson: ContactPersonInfo) {
    if (!this.canEditProjectDataInPortal) {
      return ButtonState.DISABLED;
    }

    return this.calculateSectionState(
      this.contactPersonSection(contactPerson),
      {
        canEditSection: this.canEditContactPersons,
      }
    );
  }

  showCreateContactPersonButton(contactPerson: ContactPersonInfo) {
    if (!this.canEditProjectDataInPortal) {
      return false;
    }

    if (!this.canEditContactPersons) {
      return false;
    }

    const clientData = this.clientData;

    return (
      !this.isNewProject &&
      clientData.contactPersons.indexOf(contactPerson) ===
        clientData.contactPersons.length - 1
    );
  }

  showDeleteContactPersonButton(contactPerson: ContactPersonInfo) {
    if (!this.canEditProjectDataInPortal) {
      return false;
    }

    if (!this.canEditContactPersons) {
      return false;
    }

    const clientData = this.clientData;

    return (
      !this.isNewProject &&
      clientData.contactPersons.length > 1 &&
      this.showCreateContactPersonButton(contactPerson)
    );
  }

  regulatorOptions = [
    RegulatorType.VU_FIELD_SALES,
    RegulatorType.VU_INTERNAL_SALES,
    RegulatorType.EXTERNAL_REGULATOR,
    RegulatorType.AGENT,
    RegulatorType.VU_AGENCY,
  ];

  addContactPerson() {
    this.projectDetails.clientData.contactPersons.push({
      id: generateRandomUuid(),
      contactPersonNumber:
        this.projectDetails.clientData.contactPersons.length + 1,
    });
  }

  async onDeleteContactPerson(toDelete: ContactPersonInfo) {
    const result = await this.confirmationDialogComponent.show({
      title: this.$t(
        "clientData.deleteContactPartnerConfirmationTitle"
      ).toString(),
      message: this.$t("clientData.deleteContactPartnerConfirmationMessage", [
        toDelete.contactPersonNumber,
      ]).toString(),
    });

    if (result) {
      this.$emit("deleteContactPerson", toDelete);
    }
  }

  async onAddContactPerson() {
    const contactPersons = this.clientData.contactPersons;
    const lastContactPerson = contactPersons[contactPersons.length - 1];

    const newContactPerson = {
      id: generateRandomUuid(),
      contactPersonNumber: lastContactPerson.contactPersonNumber + 1,
    };

    contactPersons.push(newContactPerson);

    this.projectDetails.clientData.contactPersons = contactPersons;
    this.onProjectChanged(clone(this.projectDetails));

    const info: ContactPersonEventInfo = { contactPersonNumber: newContactPerson.contactPersonNumber };
    const eventLog = createProjectEventLog(
      ProjectEventType.CONTACT_PERSON_ADDED,
      getRecordInfo(),
      JSON.stringify(info)
    );
    addProjectEventLog(this.projectDetails.id, eventLog);

    await this.onOpenContactPersonSection(newContactPerson);
  }

  get showContactSection() {
    if (this.projectDetails && isPortalOnlyProject(this.projectDetails)) {
      return true;
    }

    return this.isNewProject
      ? getTargetAppVersion()
      : (this.projectDetails.createdWithAppVersion ?? 0) >= 998;
  }

  get contractTypeString() {
    return isDryingProject(this.projectDetails.id)
      ? this.$t("common.drying")
      : this.$t("common.leakDetection");
  }

  get internalProjectNumber() {
    return buildIresProjectNumber(this.projectDetails);
  }
}
